/**
 * Mixin methods that initiate callpanel application
 */
import { mapGetters, mapActions } from 'vuex'
import events from 'Store/events'
import { NotifyPermissions } from 'Store/notify'
import API from '@/api'
import { parseTelLink } from '@/helpers'
import EmbeddedLocalStorage from '@/api/embedded'

export default {
  computed: {
    ...mapGetters({
      clearlyAnywhereLandingPage: 'settings/clearlyAnywhereLandingPage'
    }),
    embedded () {
      return EmbeddedLocalStorage.get()
    }
  },
  methods: {
    ...mapActions({
      appInit: 'appInit',
      authSet: 'authSet',
      setCallPanelInitialized: 'setCallPanelInitialized',
      getRingStrategies: 'ringStrategies/get',
      setFeatureCodes: 'settings/setFeatureCodes',
      loadNotifications: 'settings/getNotifications',
      getCustomPresenceTypes: 'chat/getCustomPresenceTypes',
      getPhoneNumbers: 'contacts/phoneNumbers/get',
      getAllUsers: 'users/getAllUsers',
      initBranding: 'branding/initBranding'
    }),
    async initCallpanel () {
      await this.setCallPanelInitialized()
      await this.authSet()
      return await this.appInitLoadData()
    },
    appInitLoadData () {
      events.mask()
      return this.appInit().then(() => {
        this.dbg('Application init successful after login')
        // Handle post-login redirects and click-to-dial requests
        let redirect = null
        let dialNumber = null
        let clearlyAnywhereLandingPage = null
        switch (this.$route.name) {
          case 'login':
          case 'embedded_login':
            if (this.$route?.query?.redirect === 'chat') {
              clearlyAnywhereLandingPage = '/chat'
            }
            break
          case 'oauth2': {
            const rawState = decodeURIComponent(this.$route?.query?.state || '/')
            // Prevent redirect loop
            if (!rawState || rawState.startsWith('/clusterpbx_callback')) {
              redirect = '/'
            } else {
              redirect = rawState
            }
            for (const prefix of ['dialer/', '/dialer/']) {
              if (redirect.startsWith(prefix)) {
                dialNumber = parseTelLink(redirect.substring(prefix.length))
                break
              }
            }
            break
          }
          case 'dialer':
            dialNumber = parseTelLink(
              decodeURIComponent(this.$route?.params?.phoneNumber || '')
            )
            break
        }

        if (this.embedded && clearlyAnywhereLandingPage === null) {
          clearlyAnywhereLandingPage = this.clearlyAnywhereLandingPage
        }

        if (dialNumber) {
          this.$router.push('/')

          this.dbg(`Attempting click-to-dial to '${dialNumber}' from route '${this.$route.name}'.`)
          events.pubDialerCallback(dialNumber)
        } else if (redirect) {
          this.dbg(`Redirecting to '${redirect}' from route '${this.$route.name}'.`)
          this.$router.push(redirect)
        } else if (clearlyAnywhereLandingPage) {
          this.$router.push({ path: clearlyAnywhereLandingPage })
        }

        NotifyPermissions()
        this.setFavicon()
        this.loadNotifications()
        this.getCustomPresenceTypes()
        this.getPhoneNumbers()
        this.getAllUsers()
        this.getRingStrategies()
        this.initBranding()
        // non-critical data. If fails will setup default hardcoded codes
        return this.setFeatureCodes()
      }).catch(err => {
        events.pubError(`Failed to initiate application from API: ${err}`)
        this.dbg(err)
        if (this.$route.name === 'login') {
          this.$router.push('/')
        }
      }).finally(() => {
        events.unmask()
      })
    },
    setFavicon () {
      API.getFaviconSizes().then(resp => {
        resp.data.favicon_sizes.forEach((size) => {
          const url = this.faviconUrl(API.baseURL(), size)
          document.querySelector(`#favicon_${size}`).setAttribute('href', url)
        })
      }).catch(err => {
        document.querySelector('#favicon_32').href = '/clearlyip-icon-32.png'
        this.dbg(err)
      })
    },
    faviconUrl (baseUrl, size) {
      try {
        const url = new URL(baseUrl)
        url.hostname = url.hostname.replace(/^(call)?panel\./, '')
        url.pathname = `/favicon/${size}`
        return url.toString()
      } catch (e) {
        return null
      }
    }
  }
}
